/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line import/no-extraneous-dependencies
import { isDate, isTime } from "helpers";
import { dayjsFormatTZ, dayjsTZ, dayjsTZWithDefault } from "helpers/dayjs";
import { utils, CellObject, writeFile, Range } from "xlsx-js-style";

type CellObjectExcel = CellObject | string;

const BORDER_ALL = {
  border: {
    top: { style: "thin", color: { rgb: "000000" } },
    right: { style: "thin", color: { rgb: "000000" } },
    bottom: { style: "thin", color: { rgb: "000000" } },
    left: { style: "thin", color: { rgb: "000000" } },
  },
};

export enum DataType {
  Percent = "percent",
  Number = "number",
  String = "string",
  Date = "date",
}

interface DataItemStyle {
  color: string;
  fontWeight: string;
  background: string;
  fontFamily: string;
  fontSize: string;
  textAlign: string;
}

export interface DataItem {
  value: string;
  dataType: string;
  style?: DataItemStyle;
}

export type DataExcel = DataItem[][];

export const formatColorValue = (text: string): string =>
  text?.replace("#", "");

const convertStringToNumber = (val: string): number | string => {
  if (
    (val?.match(/\./g) || []).length <= 1 &&
    (val?.match(/,/g) || []).length === 0
  ) {
    return parseFloat(val);
  }

  const num = parseFloat(val.replace(/,/g, "")); // 13,743.50 => 13743.50

  return Number.isNaN(num) ? val : num;
};

export function convertExcelDateToJSDate(serial: number) {
  // Excel date is the number of days since 1900-01-01, but Excel incorrectly considers 1900 as a leap year
  const excelEpoch = new Date(1899, 11, 30); // 1899-12-30 is the day before 1900-01-01
  const jsDate = new Date(excelEpoch.getTime() + serial * 86400000); // 86400000 ms in a day
  return jsDate;
}

export const getCellWithStyle = ({
  value,
  bold,
  italic,
  underline,
  sz,
  color,
  bgColor,
  textAlign,
  fontName,
  dataType,
  noBorder = true,
  border = [],
  formula,
  wrapText = true,
}: {
  value: string | number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  noBorder?: boolean;
  sz?: number;
  bgColor?: string;
  color?: string;
  textAlign?: string;
  fontName?: string;
  dataType?: DataType;
  border?: ("top" | "right" | "bottom" | "left")[];
  formula?: string;
  wrapText?: boolean;
}): CellObject => {
  let t = "s"; // Type string
  let z = "@";
  let v: string | number = value;
  let alignmentHorizontal = textAlign || "left";
  let f = "";

  if (dataType === DataType.Percent && value) {
    t = "n"; // Type number
    z = "0.00%";
    v = Number(convertStringToNumber(String(value || ""))) / 100;
  }

  if (dataType === DataType.Number) {
    alignmentHorizontal = "right";
    t = "n";

    const validValue = !!value; // Exclusve `""`, `null`, `undefined`
    const numValue = Number(value);
    v = !validValue || Number.isNaN(numValue) ? 0 : numValue;
    const separator = value.toString().split(".")[1] ?? "";
    z =
      separator.length > 0
        ? `#,##0.${Array(separator.length).fill("0").join("")}`
        : "#,##0";
  }

  if (dataType === DataType.Date && value) {
    t = "d";
    z = "dd/mm/yyyy";
    v = dayjsTZ(value).toString();
  }

  if (t === "s") {
    v = v || ""; // Avoid null or undefined
  }

  if (formula) {
    if (dataType === DataType.Number) z = "#,##0.0";
    f = formula;
  }

  return {
    v,
    t,
    z,
    s: {
      font: {
        ...(bold && { bold: true }),
        ...(italic && { italic: true }),
        ...(underline && { underline: true }),
        color: { rgb: color || "000000" },
        sz: sz || 10,
        name: fontName || "Arial",
      },
      alignment: {
        horizontal: alignmentHorizontal,
        vertical: "center",
        wrapText,
      },
      ...(bgColor && { fill: { fgColor: { rgb: bgColor }, bgColor } }),
      ...(!noBorder && BORDER_ALL),
      ...(border?.includes("top") && {
        border: { top: { style: "thin", color: { rgb: "000000" } } },
      }),
      ...(border?.includes("bottom") && {
        border: { bottom: { style: "thin", color: { rgb: "000000" } } },
      }),
      ...(border?.includes("left") && {
        border: { left: { style: "thin", color: { rgb: "000000" } } },
      }),
      ...(border?.includes("right") && {
        border: { right: { style: "thin", color: { rgb: "000000" } } },
      }),
    },
    ...(f && { f }),
  } as CellObject;
};

export const getDataType = (col: any, value: string): DataType => {
  if (col?.type === "head") return DataType.String;
  if (col?.dataType) return col?.dataType as DataType;

  if (col?.dataType === DataType.Number) {
    if (String(value || "")?.includes("%")) return DataType.Percent;
    return DataType.Number;
  }

  return DataType.String;
};

export const mappingStyle = (col: any, value: string) => ({
  value: value || "",
  bold: col?.style?.fontWeight === "bold",
  color: formatColorValue(String(col?.style?.color || "")),
  bgColor: formatColorValue(String(col?.style?.background || "")),
  textAlign: String(col?.style?.textAlign || ""),
  dataType: getDataType(col, value),
});

export const renderTableExcel = ({ data }: { data: DataExcel }) => {
  const result: CellObjectExcel[][] = [] as CellObjectExcel[][];

  data.forEach((row) => {
    const rowExcel = [] as CellObjectExcel[];
    row.forEach((col) => {
      rowExcel.push(getCellWithStyle(mappingStyle(col, col.value)));
    });
    result.push(rowExcel);
  });
  const ws = utils.aoa_to_sheet([result?.[0]]);
  result?.shift();

  // Render to sheet file
  utils.sheet_add_aoa(ws, [...(result || [])], {
    origin: -1,
  });

  return ws;
};

export function writeFileExcel({
  data,
  fileName,
}: {
  data: DataExcel;
  fileName: string;
}) {
  const workbook = utils.book_new();

  const ws = renderTableExcel({ data });
  utils.book_append_sheet(workbook, ws, fileName);

  writeFile(workbook, `${fileName}.xlsx`, {
    bookType: "xlsx",
    type: "buffer",
  });
}

export function writeFileTemplate({
  fileName,
  sheetName,
  data,
  merges,
  widths,
  heights,
}: {
  fileName: string;
  sheetName?: string;
  data: any[][];
  merges: Range[];
  widths: { width: number }[];
  heights?: { hpx: number }[];
}) {
  const workbook = utils.book_new();
  const ws = utils.aoa_to_sheet(data);
  utils.book_append_sheet(workbook, ws, sheetName || fileName);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ws["!merges"] = [...(ws["!merges"] || []), ...merges];
  if (widths) {
    ws["!cols"] = widths;
  }
  if (heights) {
    ws["!rows"] = heights;
  }

  writeFile(workbook, `${fileName}.xlsx`, {
    bookType: "xlsx",
    type: "buffer",
  });
}

export function writeFileMultiTemplate({
  fileName,
  dataByTab,
}: {
  fileName: string;
  dataByTab: {
    name: string;
    data: any[][];
    merges: Range[];
    widths: { width: number }[];
    heights?: { hpx: number }[];
  }[];
}) {
  const workbook = utils.book_new();

  // for each dataByTab
  dataByTab.forEach((tab) => {
    const ws = utils.aoa_to_sheet(tab.data);
    utils.book_append_sheet(workbook, ws, tab.name);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ws["!merges"] = [...(ws["!merges"] || []), ...tab.merges];
    if (tab.widths) {
      ws["!cols"] = tab.widths;
    }
    if (tab.heights) {
      ws["!rows"] = tab.heights;
    }
  });

  writeFile(workbook, `${fileName}.xlsx`, {
    bookType: "xlsx",
    type: "buffer",
  });
}

export function getWidthsXlsx(widths: number[]): { width: number }[] {
  return widths.map((width) => ({ width }));
}

export function getHeightsXlsx(heights: number[]): { hpx: number }[] {
  return heights.map((hpx) => ({ hpx }));
}

export function getMergesXlsx(params: string[][]): Range[] {
  return params.map(([start, end, init]) => {
    const [s1, s2] = start.split(":");
    const [e1, e2] = end.split(":");
    const [initRow = 0, initCol = 0] = init?.split(":") || [];
    return {
      s: {
        r: Number(s1) - 1 + Number(initRow),
        c: Number(s2) - 1 + Number(initCol),
      },
      e: {
        r: Number(e1) - 1 + Number(initRow),
        c: Number(e2) - 1 + Number(initCol),
      },
    };
  });
}

export function getEmptyXlsx(
  total: number,
  config?: Partial<Parameters<typeof getCellWithStyle>[0]>
): any[] {
  const empty = getCellWithStyle({ value: "", ...(config && config) });
  return Array(total).fill(empty);
}

export const convertHourXlsx = (value: any, subfix?: string): string => {
  if (value && typeof value === "number") {
    const dateJS = convertExcelDateToJSDate(value);
    const hours = dateJS.getHours();
    const minutes = dateJS.getMinutes();
    const hoursString = hours < 10 ? `0${hours}` : hours;
    const minutesString = minutes < 10 ? `0${minutes}` : minutes;

    return `${hoursString}:${minutesString}${subfix || ""}`;
  }
  if (!isTime(value as string)) return "";
  return (
    `${dayjsFormatTZ(String(value), "HH:mm").format("HH:mm")}${subfix || ""}` ||
    ""
  );
};

export const convertDateXlsx = (
  value: any,
  format?: string,
  inputFormat?: string
): string => {
  if (value && typeof value === "number") {
    return (
      dayjsTZWithDefault(convertExcelDateToJSDate(value)).format(
        format || "DD/MM/YYYY"
      ) || ""
    );
  }
  if (inputFormat && value) {
    return (
      dayjsFormatTZ(String(value), inputFormat || "DD/MM/YYYY").format(
        format || "DD/MM/YYYY"
      ) || ""
    );
  }
  if (!isDate(value as string)) return "";
  return (
    dayjsFormatTZ(String(value), inputFormat || "DD/MM/YYYY").format(
      format || "DD/MM/YYYY"
    ) || ""
  );
};

export const getJSONTemplateMasterData = (
  title: string,
  data: {
    code: string;
    name: string;
    nativeName: string;
  }[]
) => {
  const cell = (
    value: string,
    config?: Partial<Parameters<typeof getCellWithStyle>[0]>
  ) => {
    return getCellWithStyle({
      sz: 10,
      ...config,
      value,
    });
  };

  return [
    [],
    [cell(title, { noBorder: true, bold: true, textAlign: "left" })],
    [
      cell("Mã / Code", {
        bold: true,
        textAlign: "left",
        bgColor: "D9D9D9",
        noBorder: false,
      }),
      cell("Tên / Name", {
        bold: true,
        textAlign: "left",
        bgColor: "D9D9D9",
        noBorder: false,
      }),
    ],
    ...(data?.map((e) => [
      cell(e.code, { noBorder: false, color: "B74C27" }),
      cell(`${e.nativeName}${e.name ? ` / ${e.name}` : ""}`, {
        noBorder: false,
      }),
    ]) as never),
  ];
};

export const getJSONTemplateByConfig = (
  title: string,
  data: any[],
  table: {
    fields: string[];
    headers: string[];
    styleHeader?: Partial<Parameters<typeof getCellWithStyle>[0]>;
  }
) => {
  const cell = (
    value: string,
    config?: Partial<Parameters<typeof getCellWithStyle>[0]>
  ) => {
    return getCellWithStyle({
      sz: 10,
      ...config,
      value,
    });
  };

  return [
    [],
    [cell(title, { noBorder: true, bold: true, textAlign: "left" })],
    [
      ...table.headers.map((header) =>
        cell(
          header,
          table?.styleHeader || {
            bold: true,
            textAlign: "left",
            bgColor: "D9D9D9",
            noBorder: false,
          }
        )
      ),
    ],
    ...(data?.map((e) =>
      table.fields.map((i) => cell(String(e[i] || ""), { noBorder: false }))
    ) as never),
  ];
};

export const getContrastTextColor = (hex: string): string => {
  const sanitized = hex?.replace("#", "").trim() || "";
  const normalized =
    sanitized.length === 3
      ? sanitized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : sanitized;
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return "#000000";
  }

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);

  const brightness = r * 0.299 + g * 0.587 + b * 0.114;
  return brightness > 186 ? "#000000" : "#FFFFFF";
};
